import { useCallback, useMemo, useState } from 'react'

import { randUuid } from '@ngneat/falso'
import { format } from 'date-fns'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import {
  CustomTimePopup,
  CustomTimePopupFormValues,
} from '@/modules/automation-settings/components/custom-time-popup.tsx'
import { AutomationService } from '@/modules/automation-settings/service/automation-settings-service.ts'
import {
  AutomationItem,
  automationRunBehaviorItems,
} from '@/modules/creator-settings/data/automation-items.ts'

function getFormattedTime(date: Date) {
  const timeFormat = 'hh:mmaaa'

  return format(date, timeFormat)
}

interface RunBehaviorSelectProps {
  item: AutomationItem
  itemTitle: string
  service: AutomationService | undefined
  handleRunBehaviorChange: (
    serviceType: string,
    runBehavior: string,
    fromTimeDate?: Date,
    toTimeDate?: Date,
  ) => void
}

export const RunBehaviorSelect = ({
  item,
  itemTitle,
  service,
  handleRunBehaviorChange,
}: RunBehaviorSelectProps) => {
  const [lastSelectedValue, setLastSelectedValue] = useState<
    string | undefined
  >()
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    service?.runBehavior || 'RUN_WHEN_ONLINE',
  )
  const [customTimePopupOpen, setCustomTimePopupOpen] = useState(false)
  const [times, setTimes] = useState({
    fromTime: service?.fromTime,
    toTime: service?.toTime,
  })

  const handleCustomTimePopupClose = useCallback(() => {
    setCustomTimePopupOpen(false)
    setSelectedValue(lastSelectedValue || 'RUN_WHEN_ONLINE')
  }, [lastSelectedValue])

  const handleCustomTimePopupUpdate = useCallback(
    ({ fromTime, toTime }: CustomTimePopupFormValues) => {
      setCustomTimePopupOpen(false)

      setTimes({ fromTime, toTime })
      handleRunBehaviorChange(
        service?.serviceType || '',
        'CUSTOM_TIMES',
        fromTime,
        toTime,
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleRunBehaviorChange],
  )

  const isCustomTimesActive = useMemo(() => {
    const isCustomTimesSelected =
      selectedValue?.startsWith('CUSTOM_TIMES') ||
      (!selectedValue && service?.runBehavior?.startsWith('CUSTOM_TIMES'))
    const hasValidTimes = times?.fromTime && times?.toTime

    return isCustomTimesSelected && !!hasValidTimes
  }, [selectedValue, service, times])

  return (
    <Select
      onValueChange={(newValue: string) => {
        setLastSelectedValue(selectedValue)
        setSelectedValue(newValue)
        if (newValue.startsWith('CUSTOM_TIMES')) {
          setCustomTimePopupOpen(true)

          return
        }

        handleRunBehaviorChange(item.type, newValue)
      }}
      value={selectedValue}
    >
      <SelectTrigger className="w-72">
        {isCustomTimesActive ? (
          <div
            className="flex items-center gap-1"
            aria-label={selectedValue}
          >
            <span>Run from</span>
            <span>
              {getFormattedTime(times.fromTime!)} {'to '}
              {getFormattedTime(times.toTime!)}
            </span>
          </div>
        ) : (
          <SelectValue aria-label={selectedValue} />
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {automationRunBehaviorItems.map((option) => (
            <SelectItem
              key={option.type}
              value={
                option.type === 'CUSTOM_TIMES' && !!service
                  ? `${option.type}-${randUuid()}`
                  : option.type
              }
            >
              {option.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>

      <CustomTimePopup
        title={itemTitle}
        fromTime={service?.fromTime}
        toTime={service?.toTime}
        open={customTimePopupOpen}
        onClose={handleCustomTimePopupClose}
        update={handleCustomTimePopupUpdate}
      />
    </Select>
  )
}
